import { memo, useEffect, useState } from 'react';
import Filters from 'components/Globals/Filters';
import Dropdown from 'components/Globals/Base/Dropdown';
import RangeInput from 'components/Globals/Base/RangeInput';
import { CompaniesFilters, FilterItem, FilterRange, RankingFilterItem } from 'api/fragments';
import { STEP } from 'constants/constants';

export interface CompanyFiltersData {
  countries: FilterItem[];
  industries: FilterItem[];
  rankings: RankingFilterItem[];
  employeeRange: FilterRange;
  revenueRange: FilterRange;
  resultsNumber: number;
}

export interface Props {
  filterData: CompanyFiltersData;
  defaultData: CompaniesFilters;
  onApplyFiltersClick: (value: CompaniesFilters) => void;
  onResetFiltersClick: () => void;
}

const CompanyFilters = ({ filterData, defaultData, onApplyFiltersClick, onResetFiltersClick }: Props) => {
  const [selectedFilters, setSelectedFilters] = useState<CompaniesFilters>(defaultData);

  const { countries, industries, rankings, revenueRange, employeeRange } = filterData;

  useEffect(() => {
    setSelectedFilters(defaultData);
  }, [defaultData]);

  const updateSelectedFilters = (selectedFilter: Partial<CompaniesFilters>) => {
    setSelectedFilters((prev) => ({ ...prev, ...selectedFilter }));
  };

  const getOptions = (filterItems: FilterItem[]) =>
    filterItems.map((val) => ({ label: val.title, value: val.databaseId }));

  const getYearOptions = () => {
    const selectedRanking = rankings.find((rank) => rank.databaseId === selectedFilters.rankingId?.toString());
    return selectedRanking ? selectedRanking.years.map((year) => ({ label: year, value: year })) : [];
  };

  const yearOptions = getYearOptions();

  return (
    <Filters<CompaniesFilters>
      defaultData={defaultData}
      disclaimer='*Please note that not all companies offer this specific type of information.'
      onApplyFiltersClick={onApplyFiltersClick}
      onResetFiltersClick={onResetFiltersClick}
      selectedFilters={selectedFilters}
      setSelectedFilters={setSelectedFilters}
    >
      <Dropdown
        className='dropdown'
        size='small'
        label='Select Country'
        placeholder='Select Country'
        selected={selectedFilters.country}
        onOptionClick={(val) => updateSelectedFilters({ country: val })}
        list={getOptions(countries)}
        hasDisclaimer
        dataCy='country-dropdown'
      />
      <Dropdown
        className='dropdown'
        size='small'
        label='Select Industry'
        placeholder='Select an Industry'
        selected={selectedFilters.industry}
        onOptionClick={(val) => updateSelectedFilters({ industry: val })}
        list={getOptions(industries)}
        hasDisclaimer
        dataCy='industry-dropdown'
      />
      <Dropdown
        className='dropdown'
        size='small'
        label='Select Fortune Ranking'
        placeholder='Choose a Ranking'
        selected={rankings.find((rank) => rank.databaseId === selectedFilters.rankingId.toString())?.title}
        onOptionClick={(val) => updateSelectedFilters({ rankingId: +val })}
        list={getOptions(rankings)}
        dataCy='ranking-dropdown'
      />
      {yearOptions.length > 0 && selectedFilters.rankingId !== -1 && selectedFilters.rankingId !== undefined && (
        <Dropdown
          className='dropdown'
          size='small'
          label='Select Year'
          placeholder='Select a Year'
          selected={selectedFilters.rankingYear}
          onOptionClick={(val) => updateSelectedFilters({ rankingYear: Number(val) })}
          list={getYearOptions()}
          dataCy='year-dropdown'
        />
      )}
      <RangeInput
        id='revenues-range'
        className='revenues-range'
        label='Revenues ($M)'
        size='small'
        hasDisclaimer
        step={STEP}
        value={selectedFilters.revenues}
        minValue={revenueRange.min}
        maxValue={revenueRange.max}
        onChange={(max, min) => updateSelectedFilters({ revenues: { max, min } })}
        isCurrency
        dataCy='revenues-range'
      />
      <RangeInput
        id='employees-range'
        className='employees-range'
        label='Number of Employees'
        size='small'
        hasDisclaimer
        step={STEP}
        value={selectedFilters.employees}
        minValue={employeeRange.min}
        maxValue={employeeRange.max}
        onChange={(max, min) => updateSelectedFilters({ employees: { max, min } })}
        dataCy='employees-range'
      />
    </Filters>
  );
};

export default memo(CompanyFilters);
